import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable, of } from 'rxjs';
import { tap } from 'rxjs/operators';
import { environment } from '@env/environment';
import { FileKind } from './file-preview.types';

interface CacheEntry {
  blob: Blob;
  lastAccess: number;
}

@Injectable({ providedIn: 'root' })
export class FilePreviewService {
  private static readonly MAX_CACHE = 5;
  private readonly cache = new Map<string, CacheEntry>();

  constructor(private http: HttpClient) {}

  fetchBlob(fileUrl: string): Observable<Blob> {
    const cached = this.cache.get(fileUrl);
    if (cached) {
      cached.lastAccess = Date.now();
      return of(cached.blob);
    }
    const encoded = encodeURIComponent(fileUrl);
    const url = `${environment.apiUrl}documents/download?fileUrl=${encoded}`;
    return this.http
      .get(url, { responseType: 'blob' })
      .pipe(tap(blob => this.put(fileUrl, blob)));
  }

  detectKind(fileNameOrUrl: string, mimeType?: string): FileKind {
    const cleanName = this.stripQuery(fileNameOrUrl);
    const ext = cleanName.split('.').pop()?.toLowerCase() ?? '';
    switch (ext) {
      case 'pdf':
        return 'pdf';
      case 'docx':
        return 'docx';
      case 'xlsx':
        return 'xlsx';
      case 'jpg':
      case 'jpeg':
      case 'png':
      case 'gif':
      case 'webp':
      case 'bmp':
      case 'svg':
        return 'image';
      case 'txt':
      case 'csv':
      case 'xml':
      case 'json':
      case 'md':
      case 'log':
        return 'text';
    }
    if (mimeType) {
      if (mimeType === 'application/pdf') return 'pdf';
      if (mimeType.startsWith('image/')) return 'image';
      if (mimeType.startsWith('text/')) return 'text';
      if (mimeType === 'application/vnd.openxmlformats-officedocument.wordprocessingml.document') return 'docx';
      if (mimeType === 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet') return 'xlsx';
    }
    return 'unsupported';
  }

  release(fileUrl: string): void {
    this.cache.delete(fileUrl);
  }

  releaseAll(): void {
    this.cache.clear();
  }

  extractFileName(fileUrl: string): string {
    try {
      const base = fileUrl.substring(fileUrl.lastIndexOf('/') + 1);
      const noQuery = this.stripQuery(base);
      return decodeURIComponent(noQuery);
    } catch {
      return fileUrl;
    }
  }

  private stripQuery(s: string): string {
    const q = s.indexOf('?');
    return q >= 0 ? s.substring(0, q) : s;
  }

  private put(fileUrl: string, blob: Blob): void {
    if (this.cache.size >= FilePreviewService.MAX_CACHE) {
      this.evictOldest();
    }
    this.cache.set(fileUrl, { blob, lastAccess: Date.now() });
  }

  private evictOldest(): void {
    let oldestKey: string | null = null;
    let oldestTime = Number.POSITIVE_INFINITY;
    for (const [key, entry] of this.cache.entries()) {
      if (entry.lastAccess < oldestTime) {
        oldestTime = entry.lastAccess;
        oldestKey = key;
      }
    }
    if (oldestKey) this.release(oldestKey);
  }
}
